import React from "react";
import { makeStyles, Paper, Typography, Grid } from "@material-ui/core";
import { i18n } from "../../../translate/i18n";
import InfoTip from "./InfoTip";

const WA_GREEN = "#00a884";

const useStyles = makeStyles((theme) => ({
  section: { padding: theme.spacing(2) },
  title: { marginBottom: theme.spacing(1), fontWeight: 600 },
  barRow: { display: "flex", alignItems: "center", gap: theme.spacing(1), marginBottom: 6 },
  barLabel: { width: 110, fontSize: 13 },
  barTrack: { flex: 1, height: 20, background: "#eee", borderRadius: 3, overflow: "hidden" },
  barFill: { height: "100%", background: WA_GREEN },
  barVal: { width: 40, textAlign: "right", fontSize: 13, fontWeight: 600 },
  kpiValue: { fontSize: 22, fontWeight: 700 },
  kpiLabel: { fontSize: 12, color: "#555" },
  kpiSub: { fontSize: 11, color: "#999" },
  kpiGrid: { marginTop: theme.spacing(1.5) },
}));

const pct = (frac) => `${Math.round((frac ?? 0) * 100)}%`;

const fmtSec = (s) => {
  if (!s || s <= 0) return "—";
  if (s < 60) return `${Math.round(s)}${i18n.t("aiAssistantStats.units.seg")}`;
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return `${m}${i18n.t("aiAssistantStats.units.min")} ${r}${i18n.t("aiAssistantStats.units.seg")}`;
};

const FunnelOutcome = ({ stats, isTV = false }) => {
  const classes = useStyles();
  const ob = stats?.outcomeBreakdown;
  const enviados = stats?.enviados ?? 0;
  const respondieron = stats?.respondieron ?? 0;
  const clasificados = (ob?.satisfecho?.count ?? 0) + (ob?.reclamo?.count ?? 0);
  const maxFunnel = Math.max(enviados, 1);
  const humanAttn = (stats?.avgHumanFirstResponseSec ?? 0) > 0;

  const funnelRows = [
    { label: i18n.t("aiAssistantStats.funnel.enviados"), v: enviados },
    { label: i18n.t("aiAssistantStats.funnel.respondieron"), v: respondieron },
    { label: i18n.t("aiAssistantStats.funnel.clasificados"), v: clasificados },
  ];

  const outcomeRows = [
    { label: i18n.t("aiAssistantStats.kpis.satisfechos"), tip: "satisfechos", node: ob?.satisfecho },
    { label: i18n.t("aiAssistantStats.kpis.reclamos"), tip: "reclamos", node: ob?.reclamo },
    { label: i18n.t("aiAssistantStats.kpis.sinRespuesta"), tip: "sinRespuesta", node: ob?.sin_respuesta },
  ];

  return (
    <Paper className={classes.section} elevation={2}>
      <Typography className={classes.title}>
        {i18n.t("aiAssistantStats.funnel.title")}
        <InfoTip tipKey="embudo" isTV={isTV} />
      </Typography>

      {funnelRows.map((row, i) => (
        <div className={classes.barRow} key={i}>
          <div className={classes.barLabel}>{row.label}</div>
          <div className={classes.barTrack}>
            <div
              className={classes.barFill}
              style={{ width: `${(row.v / maxFunnel) * 100}%` }}
            />
          </div>
          <div className={classes.barVal}>{row.v}</div>
        </div>
      ))}

      <Grid container spacing={1} className={classes.kpiGrid}>
        {outcomeRows.map(({ label, tip, node }) => (
          <Grid item xs={4} key={label}>
            <div className={classes.kpiValue}>{node?.count ?? 0}</div>
            <div className={classes.kpiLabel}>
              {label}
              <InfoTip tipKey={tip} isTV={isTV} />
            </div>
            <div className={classes.kpiSub}>
              {pct(node?.pctTotal)} {i18n.t("aiAssistantStats.kpis.sobreTotal")}
            </div>
            <div className={classes.kpiSub}>
              {pct(node?.pctResponded)} {i18n.t("aiAssistantStats.kpis.sobreRespondieron")}
            </div>
          </Grid>
        ))}
        <Grid item xs={6} sm={4}>
          <div className={classes.kpiValue}>{pct(stats?.tasaRespuesta)}</div>
          <div className={classes.kpiLabel}>
            {i18n.t("aiAssistantStats.kpis.tasaRespuesta")}
            <InfoTip tipKey="tasaRespuesta" isTV={isTV} />
          </div>
        </Grid>
        <Grid item xs={6} sm={4}>
          <div className={classes.kpiValue}>{pct(stats?.tasaAccionSofia)}</div>
          <div className={classes.kpiLabel}>
            {i18n.t("aiAssistantStats.kpis.tasaAccion")}
            <InfoTip tipKey="tasaAccion" isTV={isTV} />
          </div>
        </Grid>
        <Grid item xs={6} sm={4}>
          <div className={classes.kpiValue}>{fmtSec(stats?.avgFirstResponseSec)}</div>
          <div className={classes.kpiLabel}>
            {i18n.t("aiAssistantStats.kpis.primeraRespuesta")}
            <InfoTip tipKey="primeraRespuesta" isTV={isTV} />
          </div>
        </Grid>
        <Grid item xs={6} sm={4}>
          <div className={classes.kpiValue}>
            {humanAttn
              ? i18n.t("aiAssistantStats.kpis.si")
              : i18n.t("aiAssistantStats.kpis.no")}
          </div>
          <div className={classes.kpiLabel}>
            {i18n.t("aiAssistantStats.kpis.atencionHumana")}
            <InfoTip tipKey="atencionHumana" isTV={isTV} />
          </div>
          <div className={classes.kpiSub}>
            {humanAttn
              ? i18n.t("aiAssistantStats.kpis.atendidoHorario")
              : i18n.t("aiAssistantStats.kpis.atendidoCierre")}
          </div>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default FunnelOutcome;
